import * as Sentry from '@sentry/nextjs';
import type { SupabaseClient } from '@supabase/supabase-js';
import type { Database } from '@/types/supabase';
import type { CourtDefinition } from '@/app/[locale]/admin/event-management/[eventId]/edit/scheduler/match-scheduler-types';
import { eventVenueModel, type EventVenueRow } from '@/models/eventVenueModel';
import { readEventHost, type EventHost } from './eventHost';
import {
  buildVenueContext,
  venueSummary,
  type EventVenueContext,
  type VenueLocation,
} from './venues';

type Client = SupabaseClient<Database>;

/** Court definitions off a raw `event.settings` value; `[]` for anything malformed. */
export function courtDefinitionsOf(settings: unknown): CourtDefinition[] {
  const defs = (settings as { matchScheduler?: { courtDefinitions?: unknown } } | null)
    ?.matchScheduler?.courtDefinitions;
  return Array.isArray(defs) ? (defs as CourtDefinition[]) : [];
}

/**
 * The migration may be applied after the code deploys. Postgres reports a
 * missing table as 42P01; PostgREST (supabase-js) as PGRST205 "Could not find
 * the table 'public.event_venue' in the schema cache".
 *
 * A coded error (e.g. 42501 permission denied, 42703 missing column) must
 * surface, never be treated as "table not migrated yet".
 */
export function isMissingVenueTable(e: unknown): boolean {
  const code = (e as { code?: string } | null)?.code;
  const msg = (e as { message?: string } | null)?.message ?? '';
  return code === '42P01' || code === 'PGRST205' || (!code && /event_venue/.test(msg));
}

/**
 * WRITE-path reads: a missing table (migration not applied yet) is "no rows"; any other
 * error throws — a write must never proceed on an unknown venue set.
 */
export async function listVenueRowsTolerant(
  client: Client,
  eventId: string,
): Promise<EventVenueRow[]> {
  try {
    return await eventVenueModel.listByEventId(client, eventId);
  } catch (e) {
    if (isMissingVenueTable(e)) return [];
    throw e;
  }
}

/** The ids of an event's venue rows, for the settings write guard. Strict (see above). */
export async function loadKnownVenueIds(client: Client, eventId: string): Promise<Set<string>> {
  return new Set((await listVenueRowsTolerant(client, eventId)).map((r) => r.id));
}

/**
 * DISPLAY-path reads degrade, they never throw: venues decorate a label, so a failed
 * `event_venue` (or primary `location`) read must not blank a schedule, a match page or
 * an overlay. A missing table is the expected pre-migration state and is silent; any
 * other error is reported to Sentry and read as "no extra venues". Writes and the admin
 * venue list never use these — see the tolerant reads above and the Strict loader.
 */
function reportDegraded(e: unknown, read: 'event_venue' | 'location' | 'owner'): void {
  Sentry.captureException(e, { tags: { area: 'event-venues', read } });
}

type Mode = 'degrade' | 'strict';
type Read<T> = { value: T; degraded: boolean };

async function readVenueRows(
  client: Client,
  eventId: string,
  mode: Mode,
): Promise<Read<EventVenueRow[]>> {
  if (mode === 'strict')
    return { value: await listVenueRowsTolerant(client, eventId), degraded: false };
  try {
    return { value: await eventVenueModel.listByEventId(client, eventId), degraded: false };
  } catch (e) {
    if (isMissingVenueTable(e)) return { value: [], degraded: false };
    reportDegraded(e, 'event_venue');
    return { value: [], degraded: true };
  }
}

async function readPrimary(
  client: Client,
  locationId: string | null,
  mode: Mode,
): Promise<Read<VenueLocation | null>> {
  if (!locationId) return { value: null, degraded: false };
  try {
    const { data, error } = await client
      .from('location')
      .select('id, name, address, logo, website, phone')
      .eq('id', locationId)
      .maybeSingle();
    if (error) throw Object.assign(new Error(error.message), { code: error.code });
    return { value: (data as VenueLocation | null) ?? null, degraded: false };
  } catch (e) {
    if (mode === 'strict') throw e;
    reportDegraded(e, 'location');
    return { value: null, degraded: true };
  }
}

/**
 * The event's owner, read with `readEventHost` (a missing owner row falls back to the federation; a read error throws and is caught here).
 * Null with `degraded: false` when the caller's event row names no owner (a narrow event shape
 * that only needs courts and venues): the context then keeps the location-as-club host.
 */
async function readHost(client: Client, event: EventForVenues): Promise<Read<EventHost | null>> {
  // No owner named (a narrow event row): keep the location as the host, no extra read.
  if (event.owner_kind === undefined || !event.federation_id)
    return { value: null, degraded: false };
  // A decorative name: an unreadable owner (a thrown read, or no row for the owner and the event's
  // federation) degrades in every mode, strict included (the strict loaders still throw for the
  // location and venue rows).
  try {
    const host = await readEventHost(
      client,
      {
        owner_kind: event.owner_kind ?? null,
        owner_id: event.owner_id ?? null,
        federation_id: event.federation_id,
      },
      null,
    );
    if (host) return { value: host, degraded: false };
    reportDegraded(new Error('event host unreadable'), 'owner');
  } catch (e) {
    reportDegraded(e, 'owner');
  }
  return { value: null, degraded: true };
}

/** Display read: an event's extra venue rows; `[]` on any failure (reported unless the table is missing). */
export async function listVenueRowsSafe(client: Client, eventId: string): Promise<EventVenueRow[]> {
  return (await readVenueRows(client, eventId, 'degrade')).value;
}

/** Display read: extra venue rows for many events, grouped by event; empty on any failure. */
export async function listVenueRowsForEventsSafe(
  client: Client,
  eventIds: string[],
): Promise<Map<string, EventVenueRow[]>> {
  const out = new Map<string, EventVenueRow[]>();
  let rows: EventVenueRow[] = [];
  try {
    rows = await eventVenueModel.listByEventIds(client, eventIds);
  } catch (e) {
    if (!isMissingVenueTable(e)) reportDegraded(e, 'event_venue');
    return out;
  }
  for (const r of rows) {
    const list = out.get(r.event_id) ?? [];
    list.push(r);
    out.set(r.event_id, list);
  }
  return out;
}

const SUMMARY_CHUNK = 100; // `.in()` with 200+ UUIDs is a silent 414

/** Pure half of `loadVenueSummaries`: the same rule over rows already in hand. Events with nothing to show are absent. */
export function venueSummariesFromRows(
  events: { id: string; location_id: string | null }[],
  rowsByEvent: Map<string, EventVenueRow[]>,
  hostNames: Map<string, string>,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const e of events) {
    const names = buildVenueContext({
      primary: null,
      rows: rowsByEvent.get(e.id) ?? [],
      courts: [],
    }).venues.map((v) => v.name);
    const summary = venueSummary(names, (e.location_id && hostNames.get(e.location_id)) || null);
    if (summary) out[e.id] = summary;
  }
  return out;
}

/** Display read: `venueSummary` for many events, keyed by event id; events with nothing to show are absent. */
export async function loadVenueSummaries(
  client: Client,
  events: { id: string; location_id: string | null }[],
): Promise<Record<string, string>> {
  if (events.length === 0) return {};
  const rowsByEvent = new Map<string, EventVenueRow[]>();
  for (let i = 0; i < events.length; i += SUMMARY_CHUNK) {
    const ids = events.slice(i, i + SUMMARY_CHUNK).map((e) => e.id);
    for (const [id, rows] of await listVenueRowsForEventsSafe(client, ids))
      rowsByEvent.set(id, rows);
  }
  const hostIds = [
    ...new Set(
      events.filter((e) => !rowsByEvent.has(e.id) && e.location_id).map((e) => e.location_id!),
    ),
  ];
  const hostNames = new Map<string, string>();
  try {
    for (let i = 0; i < hostIds.length; i += SUMMARY_CHUNK) {
      const { data, error } = await client
        .from('location')
        .select('id, name')
        .in('id', hostIds.slice(i, i + SUMMARY_CHUNK));
      if (error) throw new Error(error.message);
      for (const l of data ?? []) hostNames.set(l.id, l.name);
    }
  } catch (e) {
    reportDegraded(e, 'location');
  }
  return venueSummariesFromRows(events, rowsByEvent, hostNames);
}

/** The owner fields are optional: callers that only need courts and venues pass a narrow row and keep the location-as-club host. */
type EventForVenues = {
  id: string;
  location_id: string | null;
  settings: unknown;
  owner_kind?: string | null;
  owner_id?: string | null;
  federation_id?: string;
};

async function loadContext(client: Client, event: EventForVenues, mode: Mode) {
  const [primary, rows, host] = await Promise.all([
    readPrimary(client, event.location_id, mode),
    readVenueRows(client, event.id, mode),
    readHost(client, event),
  ]);
  const courts = courtDefinitionsOf(event.settings);
  // Unread venue rows degrade to "none listed" (the event's own location stands in, right
  // for almost every event); an unreadable location only matters when it is that stand-in,
  // so listed venues still render without it.
  // An unreadable owner degrades to the location as the host (today's behaviour).
  const ctx = buildVenueContext({
    primary: primary.value,
    rows: rows.value,
    courts,
    host: host.value,
  });
  return { ctx, degraded: primary.degraded || rows.degraded || host.degraded };
}

/** Display loader with its status: `degraded` = a read failed and the context fell back. */
export async function loadVenueContextForEventWithStatus(
  client: Client,
  event: EventForVenues,
): Promise<{ ctx: EventVenueContext; degraded: boolean }> {
  return loadContext(client, event, 'degrade');
}

/** Display loader: never throws on a venue/location read (see above). */
export async function loadVenueContextForEvent(
  client: Client,
  event: EventForVenues,
): Promise<EventVenueContext> {
  return (await loadContext(client, event, 'degrade')).ctx;
}

/**
 * Admin loader: any read error throws (a missing table is still "no extra venues").
 * The admin venue list must show a failure, never a primary-only 200 an admin would
 * "fix" by re-adding venues that already exist.
 */
export async function loadVenueContextForEventStrict(
  client: Client,
  event: EventForVenues,
): Promise<EventVenueContext> {
  return (await loadContext(client, event, 'strict')).ctx;
}

/** Display loader by event id. A failed EVENT read still throws (nothing to render). */
export async function loadVenueContext(
  client: Client,
  eventId: string,
): Promise<EventVenueContext> {
  const { data, error } = await client
    .from('event')
    .select('id, location_id, settings')
    .eq('id', eventId)
    .maybeSingle();
  if (error) throw new Error(error.message);
  if (!data) return { venues: [], courts: [] };
  return loadVenueContextForEvent(client, data);
}
